import {
  ActionBar,
  ChakraFlex,
  FAIconName,
  SkeletonLoader,
  Text,
} from '@equip.health/ui';
import { useEditNoteDrawerContext } from '~/lib/contexts/EditNoteDrawerContext';
import { useActivePatientCarePlan } from '~/lib/hooks/care-team/useActivePatientCarePlan';
import { usePatientActivePrimaryDiagnosis } from '~/lib/hooks/care-team/useActivePatientDiagnosis';
import { useGetModalityTreatmentAttributesQuery } from '~/store/services/treatmentAttributes';
import { getCarePlanStatusMessage } from '~/lib/util/carePlanStatus.util';
import { getOpenTreatmentAttribute } from '~/lib/util/treatmentEpisodes.util';
import { useEffect, useMemo, useState } from 'react';
import { PatientNoteActiveCarePlan } from '~/components/maud/patients/note/PatientNoteActiveCarePlan';
import { useGetCarePlanForNoteQuery } from '~/store/services/carePlan';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useGetPatientTreatmentEpisodesQuery } from '~/store/services/clinicalData';
import { useEditPatientNoteFormContext } from '~/lib/contexts/EditPatientNoteFormContext';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ProblemTypeCode } from '~/lib/types/carePlan/ProblemTypeCode';
import { CarePlanProblemDialogManager } from '~/components/maud/patients/profile/tabs/careplan/CarePlanProblemDialogManager';
import AddQoLGoalModal from '~/components/maud/patients/profile/tabs/careplan/AddQoLGoalModal';
import { AddToCarePlanMenu } from '~/components/maud/patients/profile/tabs/careplan/AddToCarePlanMenu';
import { useFormikContext } from 'formik';
import { EditNoteFormData } from '~/lib/types/notes/EditNoteFormData';

export const PatientNoteCarePlanSection = () => {
  const { patientId, noteId } = useEditNoteDrawerContext();
  const { currentUser } = useGlobalContext();
  const {
    carePlanM4MedPsychProblems: isMedPsychProblemsEnabled,
    carePlanM4ModalityOptional: isModalityOptionalFlagEnabled,
    cpFrequencyNoteSigningLogic,
  } = useFlags<LDFlags>();
  const { fieldErrors, carePlanSectionRef, showFrequencyErrors } =
    useEditPatientNoteFormContext();
  const selectedNoteTemplateRevisionId =
    useFormikContext<EditNoteFormData>()?.values.noteTemplateRevision?.node;

  const {
    isLoading: isGetActivePatientCarePlanLoading,
    activeCarePlan,
    error: getActivePatientCarePlanError,
  } = useActivePatientCarePlan(patientId);

  const [problemTypeCode, setProblemTypeCode] =
    useState<ProblemTypeCode | null>(null);
  const [isAddQoLGoalModalOpen, setIsAddQoLGoalModalOpen] = useState(false);

  const {
    data: carePlanOnNote,
    isLoading: isGetCarePlanForNoteLoading,
    error: getCarePlanForNoteError,
  } = useGetCarePlanForNoteQuery({
    noteId,
    providerRole: currentUser.providerTypeDetails.name,
    noteTemplateRevisionId: selectedNoteTemplateRevisionId,
  });

  const {
    data: patientTreatmentEpisodes,
    isLoading: isGetPatientTreatmentEpisodesLoading,
    error: getPatientTreatmentEpisodesError,
  } = useGetPatientTreatmentEpisodesQuery(patientId);

  const activeTreatmentEpisode = useMemo(
    () => patientTreatmentEpisodes?.data.find((episode) => episode.inTreatment),
    [patientTreatmentEpisodes],
  );
  const {
    isLoading: isGetActivePrimaryDiagnosisLoading,
    activePrimaryDiagnosis,
  } = usePatientActivePrimaryDiagnosis(patientId);

  useEffect(() => {
    if (getCarePlanForNoteError || getPatientTreatmentEpisodesError) {
      // this component is wrapped in an error boundary, which already handles the error display
      throw new Error(
        'Unexpected error loading active care plan for patient ' + patientId,
      );
    }
  }, [getCarePlanForNoteError, getPatientTreatmentEpisodesError, patientId]);

  const { data: modalities, isLoading: isGetModalitiesLoading } =
    useGetModalityTreatmentAttributesQuery(
      activeTreatmentEpisode?.treatmentEpisodeId,
      { skip: !activeTreatmentEpisode },
    );
  const hasActiveModality = !!getOpenTreatmentAttribute(modalities);

  const canAddToCarePlan =
    !!activePrimaryDiagnosis &&
    !!activeTreatmentEpisode &&
    (isModalityOptionalFlagEnabled || hasActiveModality) &&
    !getActivePatientCarePlanError;

  return (
    <SkeletonLoader
      id="patient-note-care-plan-section"
      isLoaded={
        !isGetCarePlanForNoteLoading &&
        !isGetPatientTreatmentEpisodesLoading &&
        !isGetActivePrimaryDiagnosisLoading &&
        !isGetModalitiesLoading &&
        !isGetActivePatientCarePlanLoading
      }
    >
      <ChakraFlex
        ref={carePlanSectionRef}
        flexDirection="column"
        marginBottom="16px"
        gap="12px"
      >
        <ChakraFlex alignItems="center" justifyContent="space-between">
          <Text variant="h4">Care plan</Text>
          {isMedPsychProblemsEnabled && (
            <AddToCarePlanMenu
              buttonId="patient-note-add-to-care-plan-btn"
              isCompact
              isDisabled={!canAddToCarePlan}
              onLegacyEdProblemClick={() => {}}
              onProblemTypeSelected={setProblemTypeCode}
              onQoLGoalClick={() => setIsAddQoLGoalModalOpen(true)}
            />
          )}
        </ChakraFlex>
        {fieldErrors.carePlanError && (
          <ActionBar
            barIcon={FAIconName.exclamationTriangleRegular}
            id="patient-note-care-plan-validation-error"
            text={
              cpFrequencyNoteSigningLogic
                ? 'Please assess the missing frequencies before signing the note.'
                : 'Please assess all frequencies before signing the note.'
            }
            variant="critical"
          />
        )}
        {!isGetCarePlanForNoteLoading && !carePlanOnNote && (
          <ChakraFlex
            alignItems="center"
            borderColor="neutral.line"
            borderRadius="12px"
            borderStyle="dashed"
            borderWidth="1px"
            flexDirection="column"
            gap="16px"
            marginTop="18px"
            padding="18px"
            width="100%"
          >
            <Text color="neutral.secondary" variant="caption">
              {getCarePlanStatusMessage({
                activeTreatmentEpisode,
                hasActiveModality,
                hasActivePrimaryDiagnosis: !!activePrimaryDiagnosis,
                isModalityOptional: isModalityOptionalFlagEnabled,
              })}
            </Text>
          </ChakraFlex>
        )}
        {carePlanOnNote && (
          <>
            {!carePlanOnNote.treatmentEpisodeModality && (
              <ActionBar
                id="patient-note-care-plan-no-interventions-until-modality"
                text="Core ED interventions won't appear on the care plan until treatment modality is selected"
                variant="info"
                barIcon={FAIconName.infoCircleRegular}
              />
            )}
            <PatientNoteActiveCarePlan
              activeCarePlan={activeCarePlan}
              carePlanOnNote={carePlanOnNote}
              treatmentEpisodeId={activeTreatmentEpisode.treatmentEpisodeId}
              showFrequencyErrors={showFrequencyErrors}
            />
          </>
        )}
        {problemTypeCode && activeTreatmentEpisode && (
          <CarePlanProblemDialogManager
            noteExternalId={noteId}
            onClose={() => setProblemTypeCode(null)}
            patientId={patientId}
            problemTypeCode={problemTypeCode}
            treatmentEpisodeId={activeTreatmentEpisode.treatmentEpisodeId}
          />
        )}
        {isAddQoLGoalModalOpen && activeTreatmentEpisode && (
          <AddQoLGoalModal
            noteExternalId={noteId}
            onClose={() => setIsAddQoLGoalModalOpen(false)}
            treatmentEpisodeId={activeTreatmentEpisode.treatmentEpisodeId}
            treatmentPathwayId={activeCarePlan?.id}
          />
        )}
      </ChakraFlex>
    </SkeletonLoader>
  );
};
